/**
 * Password-reset endpoints (no bearer token — both are for signed-out users).
 *
 *   POST /auth/forgotten-password        → ask for a reset link
 *   POST /auth/forgotten-password/reset  → spend the link, set a new password
 *
 * Also here, and the one call that does need a bearer:
 *
 *   POST /auth/reset-password           → signed in, knows the current password
 *
 * Not wrapped: POST /auth/password, which sets a first password on a
 * Google-only account without asking for a current one. A change-password form
 * must never point at it.
 */

import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from './auth';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Ask for a reset link.
 *
 * Always resolves with the same message whether or not the address has an
 * account — that is the point, and the caller must not try to tell the two
 * apart. Only a malformed address fails (422).
 */
export async function requestPasswordReset(email) {
  const response = await axios.post(
    getApiUrl('auth/forgotten-password'),
    { email },
    { headers: JSON_HEADERS },
  );
  return response.data;
}

/**
 * Spend a reset token. The token is opaque: pass it through exactly as it
 * arrived in the query string.
 *
 * @throws 400 when the link is expired, already used or unknown — all three
 *   mean "ask for another one"; 422 for a password the server rejects.
 */
export async function resetForgottenPassword(token, newPassword, confirmation) {
  const response = await axios.post(
    getApiUrl('auth/forgotten-password/reset'),
    {
      token,
      new_password: newPassword,
      new_password_confirmation: confirmation,
    },
    { headers: JSON_HEADERS },
  );
  return response.data;
}

/**
 * Change the password of the signed-in user.
 *
 * @throws 400 "Current password is incorrect" — note the status: the bearer
 *   was valid, only the password was wrong, so nothing may treat this as a
 *   session failure. 400 "This account has no password set" means a
 *   Google-only account, which needs POST /auth/password instead. 422 is the
 *   usual validation shape.
 */
export async function changePassword(oldPassword, newPassword, confirmation) {
  const response = await axios.post(
    getApiUrl('auth/reset-password'),
    {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirmation: confirmation,
    },
    {
      headers: { ...JSON_HEADERS, Authorization: `Bearer ${getAccessToken()}` },
    },
  );
  return response.data;
}
